import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { churchesService } from '@/services/churches.service';
import type { ChurchAdminCreateRequest, ChurchAdminUpdateRequest } from '@/types/users';

export const useChurchAdmins = (churchId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CHURCH_ADMINS, churchId],
    queryFn: () => churchesService.getChurchAdmins(churchId ?? ''),
    enabled: !!churchId,
  });
};

export const useAddChurchAdminMutation = (churchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (admin: ChurchAdminCreateRequest) => churchesService.addChurchAdmin(churchId, admin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMINS, churchId] });
      showNotification({ title: 'Success', message: 'Admin added successfully', color: 'green' });
    },
  });
};

export const useUpdateChurchAdminMutation = (churchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, admin }: { userId: string; admin: ChurchAdminUpdateRequest }) =>
      churchesService.updateChurchAdmin(churchId, userId, admin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMINS, churchId] });
      showNotification({ title: 'Success', message: 'Admin updated successfully', color: 'green' });
    },
  });
};

export const useRemoveChurchAdminMutation = (churchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => churchesService.removeChurchAdmin(churchId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHURCH_ADMINS, churchId] });
      showNotification({ title: 'Success', message: 'Admin removed successfully', color: 'green' });
    },
  });
};
